import React, { useState, useRef, useCallback, useEffect } from 'react';
import posthog from 'posthog-js';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { useTranslation } from 'react-i18next';

import { supabase, API_URL, themes } from './utils/constants';
import { isHtmlContent, markdownToHtml, markdownToXArticlesHtml, stripHtml, extractTweetsFromContent } from './utils/richText';
import globalStyles from './utils/globalStyles';

import Toast from './components/Toast';
import ErrorMessage from './components/ErrorMessage';
import EditorToolbar from './components/EditorToolbar';
import AuthModal from './components/AuthModal';
import ResetPasswordPage from './components/ResetPasswordPage';
import ProfilePage from './components/ProfilePage';
import ThemeToggle from './components/ThemeToggle';
import LanguageSelector from './components/LanguageSelector';
// import TemplateSelector from './components/TemplateSelector';
// import ViralityScoreDisplay from './components/ViralityScoreDisplay';
// import PerformanceReportModal from './components/PerformanceReportModal';
import ExportModal from './components/ExportModal';
import BatchProcessor from './components/BatchProcessor';
import HookGenerator from './components/HookGenerator';
import SchedulingModal from './components/SchedulingModal';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import RevenueTracker from './components/RevenueTracker';

const App = () => {
  const { t } = useTranslation();

  // Auth & User State
  const [session, setSession] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Theme
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');

  // View State
  const [view, setView] = useState('upload'); // 'upload', 'editor', 'profile', 'reset-password'
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Projects
  const [projects, setProjects] = useState([]);
  const [activeProjectId, setActiveProjectId] = useState(null);

  // Usage
  const [usage, setUsage] = useState({ used: 0, limit: 3, tier: 'free' });
  const [profileInfo, setProfileInfo] = useState(null);
  const [profileData, setProfileData] = useState(null);

  // Upload State
  const [inputMode, setInputMode] = useState('youtube'); // 'youtube' or 'file'
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [outputType, setOutputType] = useState('article');
  const [outputLanguage, setOutputLanguage] = useState('auto');
  const [includeTldr, setIncludeTldr] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStage, setProcessingStage] = useState('');
  const [error, setError] = useState(null);
  const [detectedLanguage, setDetectedLanguage] = useState(null);

  // Modals
  const [showExportModal, setShowExportModal] = useState(false);
  const [showBatchProcessor, setShowBatchProcessor] = useState(false);
  const [showHookGenerator, setShowHookGenerator] = useState(false);
  const [showSchedulingModal, setShowSchedulingModal] = useState(false);
  const [showAnalyticsDashboard, setShowAnalyticsDashboard] = useState(false);
  const [showRevenueTracker, setShowRevenueTracker] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [pricingBillingCycle, setPricingBillingCycle] = useState('yearly');
  const [refineCount, setRefineCount] = useState(0);

  // Regenerate / Refine
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [regenerateFeedback, setRegenerateFeedback] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [contentVersions, setContentVersions] = useState({}); // { projectId: [prev1, prev2, ...] } max 6

  // Toast
  const [toast, setToast] = useState(null);

  // Virality Engine State
  const [viralityScore, setViralityScore] = useState(null); // eslint-disable-line no-unused-vars
  const [contentNiche, setContentNiche] = useState(null);
  const [isRescoringVirality, setIsRescoringVirality] = useState(false); // eslint-disable-line no-unused-vars

  // Refs
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  const activeProject = projects.find(p => p && p.id === activeProjectId);

  // Banner state
  const bannerInputRef = useRef(null);

  // TipTap Editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
    ],
    content: '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      updateProjectContent(html);
    },
  });

  // Sync editor content when active project changes
  useEffect(() => {
    if (editor && activeProject) {
      const html = markdownToHtml(activeProject.content || '');
      // Only update if content is actually different to avoid cursor jumps
      if (editor.getHTML() !== html) {
        editor.commands.setContent(html);
      }
    }
  }, [activeProjectId, editor]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check for password reset URL and OAuth redirect params
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('type=recovery')) {
      setView('reset-password');
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get('twitter_connected')) {
      showToast('X (Twitter) connected! You can now schedule posts.', 'success');
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (params.get('twitter_error')) {
      showToast('Failed to connect X. Please try again.', 'error');
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (params.get('stripe_connected')) {
      showToast('Stripe connected! Revenue will sync automatically.', 'success');
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (params.get('stripe_error')) {
      showToast('Failed to connect Stripe. Please try again.', 'error');
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (params.get('gumroad_connected')) {
      showToast('Gumroad connected! Sales will sync automatically.', 'success');
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (params.get('gumroad_error')) {
      showToast('Failed to connect Gumroad. Please try again.', 'error');
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (params.get('checkout') === 'success') {
      showToast('Welcome to Pro! You now have unlimited transforms and refines.', 'success');
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (params.get('checkout') === 'cancel') {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []); // eslint-disable-line

  // Apply theme
  useEffect(() => {
    const root = document.documentElement;
    const themeVars = themes[theme];
    Object.entries(themeVars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Auth listener
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) {
        loadUserData(session);
        posthog.identify(session.user.id, { email: session.user.email });
      }
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) {
        loadUserData(session);
        posthog.identify(session.user.id, { email: session.user.email });
      } else {
        posthog.reset();
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
  };

  const loadProjects = async () => {
    if (!session) return;
    try {
      const projectsRes = await fetch(`${API_URL}/api/projects`, { headers: { 'Authorization': `Bearer ${session.access_token}` } });
      if (projectsRes.ok) {
        const data = await projectsRes.json();
        setProjects((data.projects || []).filter(Boolean));
      }
    } catch (err) {
      console.error('Failed to load projects:', err);
    }
  };

  const loadUserData = async (session) => {
    try {
      const res = await fetch(`${API_URL}/api/me`, { headers: { 'Authorization': `Bearer ${session.access_token}` } });
      if (res.ok) {
        const data = await res.json();
        setUsage(data.usage);
        setProfileInfo(data.voiceProfile);
        setProfileData(data.profile || null);
      }
      const projectsRes = await fetch(`${API_URL}/api/projects`, { headers: { 'Authorization': `Bearer ${session.access_token}` } });
      if (projectsRes.ok) {
        const data = await projectsRes.json();
        setProjects((data.projects || []).filter(Boolean));
      }
      // Load refine count for gating
      const { count } = await supabase
        .from('user_style_preferences')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', session.user.id);
      setRefineCount(count || 0);
    } catch (err) {
      console.error('Failed to load user data:', err);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setProjects([]);
    setActiveProjectId(null);
    setView('upload');
    showToast(t('auth.loggedOut'), 'info');
  };

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const getFileType = (file) => {
    const audioExts = ['.mp3', '.wav', '.m4a', '.ogg', '.flac', '.aac'];
    return audioExts.includes(file.name.toLowerCase().substring(file.name.lastIndexOf('.'))) ? 'audio' : 'video';
  };

  const handleFileSelect = useCallback((selectedFile) => {
    if (selectedFile) {
      setFile(selectedFile);
      setFileType(getFileType(selectedFile));
      setError(null);
    }
  }, []);

  const handleDragOver = (e) => { e.preventDefault(); dropZoneRef.current?.classList.add('drag-over'); };
  const handleDragLeave = (e) => { e.preventDefault(); dropZoneRef.current?.classList.remove('drag-over'); };
  const handleDrop = (e) => { e.preventDefault(); dropZoneRef.current?.classList.remove('drag-over'); handleFileSelect(e.dataTransfer.files[0]); };

  const processFile = async () => {
    if (!file || !session) return;
    setIsProcessing(true);
    setError(null);
    setProcessingStage(t('upload.uploading'));
    try {
      const formData = new FormData();
      formData.append('media', file);
      formData.append('outputType', outputType);
      if (outputType === 'article') formData.append('template', 'file-matched');
      if (outputType === 'article') formData.append('include_tldr', includeTldr ? 'true' : 'false');
      if (outputLanguage !== 'auto') formData.append('outputLanguage', outputLanguage);
      setProcessingStage(t('upload.processing'));
      const response = await fetch(`${API_URL}/api/process`, { method: 'POST', headers: { 'Authorization': `Bearer ${session.access_token}` }, body: formData });
      const data = await response.json();
      if (!response.ok) throw data;
      if (!data.project || !data.project.id) throw new Error(t('errors.noProjectReturned'));
      setProjects(prev => [data.project, ...prev].filter(Boolean));
      setActiveProjectId(data.project.id);
      setUsage(data.usage);
      setProfileInfo(data.profileInfo);
      setDetectedLanguage(data.language);
      if (data.viralityScore) setViralityScore(data.viralityScore);
      if (data.niche) setContentNiche(data.niche);
      setView('editor');
      setFile(null);
      setFileType(null);
      showToast(t('upload.contentGenerated'), 'success');
    } catch (err) {
      setError(err);
    } finally {
      setIsProcessing(false);
      setProcessingStage('');
    }
  };

  const processYoutubeUrl = async () => {
    if (!youtubeUrl.trim() || !session) return;
    setIsProcessing(true);
    setError(null);
    setProcessingStage(t('upload.processingYoutube'));
    try {
      const response = await fetch(`${API_URL}/api/process-youtube`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: youtubeUrl.trim(),
          outputType,
          template: outputType === 'article' ? 'file-matched' : undefined,
          include_tldr: outputType === 'article' ? includeTldr : false,
          outputLanguage: outputLanguage !== 'auto' ? outputLanguage : undefined
        })
      });
      const data = await response.json();
      if (!response.ok) throw data;
      if (!data.project || !data.project.id) throw new Error(t('errors.noProjectReturned'));
      setProjects(prev => [data.project, ...prev].filter(Boolean));
      setActiveProjectId(data.project.id);
      setUsage(data.usage);
      setProfileInfo(data.profileInfo);
      setDetectedLanguage(data.language);
      if (data.viralityScore) setViralityScore(data.viralityScore);
      if (data.niche) setContentNiche(data.niche);
      setView('editor');
      setYoutubeUrl('');
      showToast(t('upload.contentGenerated'), 'success');
    } catch (err) {
      setError(err);
    } finally {
      setIsProcessing(false);
      setProcessingStage('');
    }
  };

  const handleProcess = () => {
    if (inputMode === 'youtube') {
      processYoutubeUrl();
    } else {
      processFile();
    }
  };

  const rescoreVirality = async () => { // eslint-disable-line no-unused-vars
    if (!activeProject || !session) return;
    setIsRescoringVirality(true);
    try {
      const plainContent = isHtmlContent(activeProject.content) ? stripHtml(activeProject.content) : activeProject.content;
      const res = await fetch(`${API_URL}/api/virality-score`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: activeProject.id, content: plainContent, contentType: activeProject.type, niche: contentNiche })
      });
      if (res.ok) {
        const data = await res.json();
        setViralityScore(data.viralityScore);
        showToast(t('virality.scoreUpdated'), 'success');
      }
    } catch (err) {
      showToast(t('virality.scoreFailed'), 'error');
    } finally {
      setIsRescoringVirality(false);
    }
  };

  const loadViralityScore = async (projectId) => {
    if (!session) return;
    try {
      const res = await fetch(`${API_URL}/api/virality-score/${projectId}`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setViralityScore(data.viralityScore);
      }
    } catch (err) {
      console.error('Failed to load virality score:', err);
    }
  };

  const updateProjectContent = async (content) => {
    setProjects(prev => prev.map(p => (p && p.id === activeProjectId) ? { ...p, content, updated_at: new Date().toISOString() } : p));
    if (session) {
      await fetch(`${API_URL}/api/projects`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: activeProjectId, content })
      });
    }
  };

  const updateProjectName = async (name) => {
    setProjects(prev => prev.map(p => (p && p.id === activeProjectId) ? { ...p, name } : p));
    if (session) {
      await fetch(`${API_URL}/api/projects`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: activeProjectId, name })
      });
    }
  };

  const updateProjectBanner = async (bannerUrl) => {
    setProjects(prev => prev.map(p => (p && p.id === activeProjectId) ? { ...p, banner_url: bannerUrl } : p));
    if (session) {
      await fetch(`${API_URL}/api/projects`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: activeProjectId, banner_url: bannerUrl })
      });
    }
  };

  const handleRegenerate = async () => {
    if (!activeProject || !session || !regenerateFeedback.trim()) return;
    setIsRegenerating(true);
    try {
      // Save current content to version history (max 6 versions)
      setContentVersions(prev => {
        const versions = prev[activeProject.id] || [];
        const updated = [activeProject.content, ...versions].slice(0, 6);
        return { ...prev, [activeProject.id]: updated };
      });

      const response = await fetch(`${API_URL}/api/regenerate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: activeProject.id, feedback: regenerateFeedback.trim() })
      });
      const data = await response.json();
      if (!response.ok) {
        if (data.error === 'upgrade_required') {
          // Remove the version we just saved since refinement didn't happen
          setContentVersions(prev => {
            const versions = prev[activeProject.id] || [];
            return { ...prev, [activeProject.id]: versions.slice(1) };
          });
          setShowRegenerateModal(false);
          setShowPricingModal(true);
          showToast(data.message, 'error');
          return;
        }
        throw data;
      }
      updateProjectContent(data.content);
      setShowRegenerateModal(false);
      setRegenerateFeedback('');
      setRefineCount(prev => prev + 1);
      showToast('Article refined!', 'success');
    } catch (err) {
      // Remove the version we just saved since refinement failed
      setContentVersions(prev => {
        const versions = prev[activeProject.id] || [];
        return { ...prev, [activeProject.id]: versions.slice(1) };
      });
      showToast(err.message || 'Failed to refine', 'error');
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleUndoRefine = () => {
    if (!activeProject) return;
    const versions = contentVersions[activeProject.id] || [];
    if (versions.length === 0) return;
    const [previousContent, ...remaining] = versions;
    updateProjectContent(previousContent);
    setContentVersions(prev => ({ ...prev, [activeProject.id]: remaining }));
    showToast('Reverted to previous version', 'success');
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      showToast(t('editor.bannerInvalidType'), 'error');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast(t('editor.bannerTooLarge'), 'error');
      return;
    }
    try {
      const ext = file.name.split('.').pop();
      const filePath = `${session.user.id}/${activeProjectId}.${ext}`;
      const { error: uploadError } = await supabase.storage.from('banners').upload(filePath, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('banners').getPublicUrl(filePath);
      await updateProjectBanner(publicUrl);
      showToast(t('editor.bannerUploaded'), 'success');
    } catch (err) {
      showToast(t('editor.bannerUploadFailed') + err.message, 'error');
    }
  };

  const removeBanner = async () => {
    try {
      if (activeProject?.banner_url) {
        const urlParts = activeProject.banner_url.split('/banners/');
        if (urlParts[1]) {
          await supabase.storage.from('banners').remove([urlParts[1]]);
        }
      }
      await updateProjectBanner(null);
      showToast(t('editor.bannerRemoved'), 'info');
    } catch (err) {
      showToast(t('editor.bannerUploadFailed') + err.message, 'error');
    }
  };

  const deleteProject = async (id) => {
    if (!window.confirm(t('sidebar.deleteConfirm'))) return;
    setProjects(prev => prev.filter(p => p && p.id !== id));
    if (activeProjectId === id) {
      setActiveProjectId(null);
      setView('upload');
    }
    if (session) {
      await fetch(`${API_URL}/api/projects/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${session.access_token}` } });
    }
    showToast(t('sidebar.projectDeleted'), 'info');
  };

  const exportAsText = () => {
    if (!activeProject) return;
    const text = isHtmlContent(activeProject.content) ? stripHtml(activeProject.content) : activeProject.content;
    const blob = new Blob([text], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${activeProject.name}.txt`;
    a.click();
    setShowExportModal(false);
    showToast(t('export.downloaded'), 'success');
  };

  const openXComposer = async () => {
    if (!activeProject?.content) return;
    if (activeProject.type === 'thread') {
      // Threads: use old tweet intent
      const text = isHtmlContent(activeProject.content) ? stripHtml(activeProject.content) : activeProject.content;
      const intentUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
      window.open(intentUrl, '_blank', 'noopener,noreferrer');
      setShowExportModal(false);
      showToast(t('export.openedXComposer'), 'success');
      return;
    }
    // Articles: copy rich HTML to clipboard + open X Articles composer
    // Use X Articles-optimized HTML that converts headers to bold (X strips h2/h3 tags)
    try {
      let htmlContent = markdownToXArticlesHtml(activeProject.content);
      if (activeProject.banner_url) {
        htmlContent = `<img src="${activeProject.banner_url}" alt="Banner" style="max-width:100%;margin-bottom:16px;" />\n` + htmlContent;
      }
      const plainText = stripHtml(htmlContent);
      if (navigator.clipboard && window.ClipboardItem) {
        const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
        const textBlob = new Blob([plainText], { type: 'text/plain' });
        await navigator.clipboard.write([new window.ClipboardItem({ 'text/html': htmlBlob, 'text/plain': textBlob })]);
      } else {
        await navigator.clipboard.writeText(plainText);
      }
      window.open('https://x.com/compose/articles', '_blank', 'noopener,noreferrer');
      setShowExportModal(false);
      showToast(t('export.contentCopied'), 'success');
    } catch (err) {
      showToast(t('export.copyFailed') + err.message, 'error');
    }
  };

  const openThreadsComposer = () => {
    if (!activeProject?.content) return;
    const plainContent = isHtmlContent(activeProject.content) ? stripHtml(activeProject.content) : activeProject.content;
    const formatted = plainContent
      .replace(/^\d+\/\s*/gm, '')
      .split('\n\n')
      .filter(p => p.trim())
      .join('\n\n---\n\n');
    const intentUrl = `https://www.threads.net/intent/post?text=${encodeURIComponent(formatted)}`;
    window.open(intentUrl, '_blank', 'noopener,noreferrer');
    setShowExportModal(false);
    showToast(t('export.openedThreads'), 'success');
  };

  const canProcess = inputMode === 'youtube' ? youtubeUrl.trim() : file;

  // Reset Password View
  if (view === 'reset-password') {
    return (
      <>
        <ResetPasswordPage onComplete={() => setView('upload')} />
        <style>{globalStyles}</style>
      </>
    );
  }

  // Profile View
  if (view === 'profile' && session) {
    return (
      <>
        <ProfilePage
          session={session}
          onBack={() => setView('upload')}
          showToast={showToast}
          onProfileUpdated={(profile) => setProfileData(prev => ({ ...(prev || {}), ...profile }))}
        />
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        <style>{globalStyles}</style>
      </>
    );
  }

  return (
    <div style={{ display: 'flex', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'var(--bg-primary)', overflow: 'hidden' }}>
      {/* Sidebar */}
      <aside style={{
        background: 'var(--bg-secondary)',
        borderRight: '1px solid var(--border-color)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
        width: sidebarCollapsed ? '60px' : '280px',
        flexShrink: 0,
        overflow: 'hidden',
        minHeight: 0
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', borderBottom: '1px solid var(--border-light)', minHeight: '60px' }}>
          {!sidebarCollapsed && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }} onClick={() => { setView('upload'); setActiveProjectId(null); setError(null); }}>
              <div style={{ fontSize: '24px' }}>⚡</div>
              <span style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>ContentTransform</span>
            </div>
          )}
          <button
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '18px', padding: '4px' }}
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            {sidebarCollapsed ? '→' : '←'}
          </button>
        </div>

        {/* User Section */}
        {!sidebarCollapsed && (
          <div style={{ padding: '16px', borderBottom: '1px solid var(--border-light)' }}>
            {session ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div
                    style={{
                      width: '40px', height: '40px', borderRadius: '50%',
                      background: 'var(--accent-gradient)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      fontSize: '16px', fontWeight: '600', color: 'white',
                      cursor: 'pointer'
                    }}
                    onClick={() => setView('profile')}
                    title="Edit profile"
                  >
                    {(profileData?.display_name || session.user.email || '?')[0]?.toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {profileData?.display_name || session.user.email}
                    </span>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      {usage.tier === 'pro' ? '⭐ ' + t('sidebar.pro') : t('sidebar.free')} • {usage.used}/{usage.limit} {t('sidebar.used')}
                    </span>
                    {usage.resetAt && (
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                        Resets {new Date(usage.resetAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <button
                    style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '18px', padding: '4px' }}
                    onClick={handleLogout}
                    title="Sign out"
                  >
                    ↪
                  </button>
                </div>
                {usage.tier !== 'pro' && (
                  <button
                    onClick={() => setShowPricingModal(true)}
                    style={{
                      width: '100%', padding: '10px', borderRadius: '10px', marginTop: '12px',
                      border: 'none', background: 'linear-gradient(135deg, #f59e0b, #f97316)',
                      color: 'white', fontSize: '13px', fontWeight: '600',
                      cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s'
                    }}
                  >
                    ⭐ Upgrade to Pro
                  </button>
                )}
              </>
            ) : (
              <button
                style={{
                  width: '100%', padding: '12px', borderRadius: '10px',
                  border: '1.5px solid var(--accent-primary)', background: 'var(--accent-subtle-bg)',
                  color: 'var(--accent-primary)', fontSize: '14px', fontWeight: '600',
                  cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s'
                }}
                onClick={() => setShowAuthModal(true)}
              >
                {t('auth.signIn')}
              </button>
            )}
          </div>
        )}

        {/* New Project */}
        <div style={{ padding: '16px', display: 'flex', gap: '8px' }}>
          <button
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              padding: '12px', borderRadius: '10px',
              border: '2px dashed var(--border-color)', background: 'transparent',
              color: 'var(--text-secondary)', fontSize: '14px', fontWeight: '500',
              cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s'
            }}
            onClick={() => { setView('upload'); setActiveProjectId(null); setError(null); }}
          >
            <span style={{ fontSize: '18px' }}>+</span>
            {!sidebarCollapsed && <span>{t('sidebar.newProject')}</span>}
          </button>
        </div>

        {/* Revenue Features */}
        {!sidebarCollapsed && session && (
          <div style={{ padding: '0 16px 16px' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '8px 4px', marginBottom: '8px' }}>
              Revenue Tools
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <button
                onClick={() => setShowBatchProcessor(true)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px',
                  borderRadius: '8px', border: 'none', background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)', fontSize: '13px', cursor: 'pointer',
                  fontFamily: 'inherit', textAlign: 'left', transition: 'all 0.2s'
                }}
              >
                <span style={{ fontSize: '16px' }}>⚡</span>
                <span>Batch Transform</span>
              </button>
              <button
                onClick={() => setShowAnalyticsDashboard(true)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px',
                  borderRadius: '8px', border: 'none', background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)', fontSize: '13px', cursor: 'pointer',
                  fontFamily: 'inherit', textAlign: 'left', transition: 'all 0.2s'
                }}
              >
                <span style={{ fontSize: '16px' }}>📊</span>
                <span>Analytics</span>
              </button>
              {/* Revenue Tracking hidden until ready — uncomment to restore
              <button
                onClick={() => setShowRevenueTracker(true)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px',
                  borderRadius: '8px', border: 'none', background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)', fontSize: '13px', cursor: 'pointer',
                  fontFamily: 'inherit', textAlign: 'left', transition: 'all 0.2s'
                }}
              >
                <span style={{ fontSize: '16px' }}>💰</span>
                <span>Revenue Tracking</span>
              </button>
              */}
            </div>
          </div>
        )}

        {/* Projects List */}
        {!sidebarCollapsed && (
          <div style={{ flex: 1, overflow: 'auto', padding: '0 12px', minHeight: 0 }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '8px 4px' }}>
              {t('sidebar.projects')}
            </div>
            {projects.length === 0 ? (
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', padding: '20px', textAlign: 'center' }}>
                {session ? t('sidebar.noProjects') : t('sidebar.signInToSave')}
              </p>
            ) : (
              projects.filter(Boolean).map(project => (
                <div
                  key={project.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '12px', borderRadius: '10px', cursor: 'pointer', marginBottom: '4px',
                    background: activeProjectId === project.id ? 'var(--accent-subtle-bg)' : 'transparent',
                    border: activeProjectId === project.id ? '1px solid var(--accent-subtle-border)' : '1px solid transparent',
                    transition: 'all 0.2s'
                  }}
                  onClick={() => { setActiveProjectId(project.id); setView('editor'); setError(null); loadViralityScore(project.id); }}
                >
                  <span style={{ fontSize: '18px' }}>{project.type === 'thread' ? '🧵' : '📝'}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <input
                      type="text"
                      value={project.name}
                      onChange={(e) => {
                        const newName = e.target.value;
                        setProjects(prev => prev.map(p => (p && p.id === project.id) ? { ...p, name: newName } : p));
                      }}
                      onBlur={(e) => {
                        const newName = e.target.value.trim();
                        if (newName && newName !== project.name) {
                          if (session) {
                            fetch(`${API_URL}/api/projects`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
                              body: JSON.stringify({ id: project.id, name: newName })
                            });
                          }
                        }
                        e.target.style.borderColor = 'var(--border-color)';
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onFocus={(e) => { e.stopPropagation(); e.target.style.borderColor = 'var(--accent-primary)'; }}
                      style={{ display: 'block', width: '100%', fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', background: 'transparent', border: '1.5px dashed var(--border-color)', borderRadius: '6px', padding: '2px 6px', outline: 'none', fontFamily: 'inherit', transition: 'border-color 0.2s', boxSizing: 'border-box' }}
                    />
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                      {new Date(project.updated_at).toLocaleDateString()}
                    </span>
                  </div>
                  <button
                    style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '16px', padding: '4px', opacity: 0.6 }}
                    onClick={(e) => { e.stopPropagation(); deleteProject(project.id); }}
                  >
                    ×
                  </button>
                </div>
              ))
            )}
          </div>
        )}

        {/* Voice Profile Indicator */}
        {!sidebarCollapsed && profileInfo?.hasProfile && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '12px 16px', margin: '12px 16px 16px',
            borderRadius: '10px', background: 'var(--accent-subtle-bg)',
            border: '1px solid var(--accent-subtle-border)',
            fontSize: '13px', color: 'var(--success)'
          }}>
            <span>✓</span>
            <span>{profileInfo.transcriptCount < 5 ? t('sidebar.voiceLearning', { count: profileInfo.transcriptCount }) : t('sidebar.voiceActive')}</span>
          </div>
        )}
      </aside>

      {/* Main Content */}
      <main style={{ flex: 1, position: 'relative', overflow: 'auto', minHeight: 0 }}>
        {/* Theme Toggle + Language Selector - Top Right (only shown fixed on upload view) */}
        {view === 'upload' && (
          <div style={{ position: 'fixed', top: '16px', right: '20px', zIndex: 500, display: 'flex', gap: '8px', alignItems: 'center' }}>
            <LanguageSelector />
            <ThemeToggle theme={theme} onToggle={toggleTheme} />
          </div>
        )}
        {/* Upload View */}
        {view === 'upload' && !session && (
          <div style={{ overflow: 'auto', height: '100%', padding: '20px 20px' }}>
            <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
              {/* Landing Hero */}
              <div style={{ marginBottom: '48px', animation: 'fadeUp 0.6s cubic-bezier(0.16, 1, 0.3, 1)' }}>
                <h1 style={{ fontSize: 'clamp(28px, 5vw, 48px)', fontWeight: '700', marginBottom: '16px', lineHeight: '1.15', letterSpacing: '-0.03em', fontFamily: "'Source Serif 4', Georgia, serif" }}>
                  <span style={{ background: 'var(--accent-gradient)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    From video to viral — grow your audience on X
                  </span>
                </h1>
                <p style={{ fontSize: '18px', color: 'var(--text-secondary)', lineHeight: '1.6', fontWeight: '500', maxWidth: '600px', margin: '0 auto 32px' }}>
                  Upload a video or paste a YouTube link. Get polished threads and articles in your unique voice, ready to publish.
                </p>
                <button
                  onClick={() => setShowAuthModal(true)}
                  style={{
                    padding: '16px 40px', borderRadius: '12px', border: 'none',
                    background: 'var(--accent-gradient)', color: 'white',
                    fontSize: '17px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit',
                    transition: 'transform 0.2s'
                  }}
                  onMouseEnter={e => e.target.style.transform = 'scale(1.03)'}
                  onMouseLeave={e => e.target.style.transform = 'scale(1)'}
                >
                  Start Free
                </button>
              </div>

              {/* Feature Cards */}
              <div style={{ display: 'flex', alignItems: 'stretch', justifyContent: 'center', gap: '0', marginBottom: '64px', flexWrap: 'wrap' }}>
                {[
                  { icon: '🧬', title: 'Voice DNA', desc: 'Your writing style is learned from your uploads. Every article sounds like you, not a robot.' },
                  { icon: '✨', title: 'Refine with AI', desc: 'Tell the AI to adjust tone, structure, or style. Your preferences are remembered for future content.' },
                  { icon: '𝕏', title: 'Post on X', desc: 'Publish your refined articles and threads directly to X and grow your audience.' }
                ].map((f, i, arr) => (
                  <React.Fragment key={i}>
                    <div style={{
                      padding: '28px', borderRadius: '16px', background: 'var(--bg-card)',
                      border: '1px solid var(--border-color)', textAlign: 'left',
                      animation: `fadeUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) ${0.1 + i * 0.1}s both`,
                      flex: '1 1 240px', maxWidth: '300px', minWidth: '240px'
                    }}>
                      <div style={{ fontSize: '32px', marginBottom: '12px' }}>{f.icon}</div>
                      <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '8px' }}>{f.title}</h3>
                      <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>{f.desc}</p>
                    </div>
                    {i < arr.length - 1 && (
                      <div style={{
                        display: 'flex', alignItems: 'center', padding: '0 12px',
                        fontSize: '28px', color: 'var(--text-muted)', fontWeight: '300',
                        animation: `fadeUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) ${0.15 + i * 0.1}s both`
                      }}>
                        →
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Pricing Table */}
              <div style={{ marginBottom: '64px' }}>
                <h2 style={{ fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '8px' }}>Simple pricing</h2>
                <p style={{ fontSize: '15px', color: 'var(--text-secondary)', marginBottom: '32px' }}>Start free. Upgrade when you're ready.</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', maxWidth: '640px', margin: '0 auto' }}>
                  {/* Free */}
                  <div style={{ padding: '28px', borderRadius: '16px', background: 'var(--bg-card)', border: '1px solid var(--border-color)', textAlign: 'left' }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '8px' }}>Free</div>
                    <div style={{ fontSize: '32px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '16px' }}>$0</div>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '2' }}>
                      <li>3 transforms per week</li>
                      <li>2 AI refines (lifetime)</li>
                      <li>Voice DNA learning</li>
                      <li>Export to Markdown</li>
                    </ul>
                    <button onClick={() => setShowAuthModal(true)} style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '2px solid var(--border-color)', background: 'transparent', color: 'var(--text-primary)', fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', marginTop: '20px' }}>
                      Get Started
                    </button>
                  </div>
                  {/* Pro */}
                  <div style={{ padding: '28px', borderRadius: '16px', background: 'var(--bg-card)', border: '2px solid var(--accent-primary)', textAlign: 'left', position: 'relative' }}>
                    <div style={{ position: 'absolute', top: '-12px', right: '16px', padding: '4px 12px', borderRadius: '20px', background: 'var(--accent-gradient)', color: 'white', fontSize: '11px', fontWeight: '700' }}>POPULAR</div>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--accent-primary)', textTransform: 'uppercase', marginBottom: '8px' }}>Pro</div>
                    <div style={{ fontSize: '32px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '4px' }}>$12<span style={{ fontSize: '16px', fontWeight: '400', color: 'var(--text-muted)' }}>/mo</span></div>
                    <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '16px' }}>$54/6mo or $99/year (save up to 31%)</div>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '2' }}>
                      <li>Unlimited transforms</li>
                      <li>Unlimited AI refines</li>
                      <li>Voice DNA learning</li>
                      <li>All export formats</li>
                      <li>Priority support</li>
                    </ul>
                    <button onClick={() => setShowAuthModal(true)} style={{ width: '100%', padding: '12px', borderRadius: '10px', border: 'none', background: 'var(--accent-gradient)', color: 'white', fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', marginTop: '20px' }}>
                      Start Free, Upgrade Later
                    </button>
                  </div>
                </div>
              </div>

              {/* Final CTA */}
              <div style={{ marginBottom: '40px' }}>
                <button
                  onClick={() => setShowAuthModal(true)}
                  style={{
                    padding: '16px 40px', borderRadius: '12px', border: 'none',
                    background: 'var(--accent-gradient)', color: 'white',
                    fontSize: '17px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit'
                  }}
                >
                  Start Free
                </button>
              </div>
            </div>
          </div>
        )}
        {view === 'upload' && session && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100%', padding: '40px 20px', overflow: 'auto', height: '100%' }}>
            <div style={{ width: '100%', maxWidth: '560px', textAlign: 'center' }}>
              {/* Hero */}
              <div style={{ marginBottom: '48px', animation: 'fadeUp 0.6s cubic-bezier(0.16, 1, 0.3, 1)' }}>
                <h1 style={{ fontSize: '40px', fontWeight: '700', marginBottom: '16px', lineHeight: '1.15', letterSpacing: '-0.02em', fontFamily: "'Source Serif 4', Georgia, serif" }}>
                  <span style={{ background: 'var(--accent-gradient)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    {t('app.tagline')}
                  </span>
                </h1>
                <p style={{ fontSize: '17px', color: 'var(--text-secondary)', lineHeight: '1.6', fontWeight: '500' }}>
                  {t('app.subtitle')}
                </p>
              </div>

              <ErrorMessage error={error} onDismiss={() => setError(null)} />

              {/* Input Mode Toggle: YouTube Link (left) | Upload File (right) */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
                <button
                  style={{
                    flex: 1, padding: '12px', borderRadius: '12px',
                    border: `2px solid ${inputMode === 'youtube' ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                    background: inputMode === 'youtube' ? 'var(--accent-subtle-bg)' : 'transparent',
                    color: inputMode === 'youtube' ? 'var(--accent-primary)' : 'var(--text-secondary)',
                    fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
                    transition: 'all 0.2s'
                  }}
                  onClick={() => setInputMode('youtube')}
                >
                  {t('upload.youtubeLink')}
                </button>
                <button
                  style={{
                    flex: 1, padding: '12px', borderRadius: '12px',
                    border: `2px solid ${inputMode === 'file' ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                    background: inputMode === 'file' ? 'var(--accent-subtle-bg)' : 'transparent',
                    color: inputMode === 'file' ? 'var(--accent-primary)' : 'var(--text-secondary)',
                    fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
                    transition: 'all 0.2s'
                  }}
                  onClick={() => setInputMode('file')}
                >
                  {t('upload.uploadFile')}
                </button>
              </div>

              {/* YouTube URL Input */}
              {inputMode === 'youtube' && (
                <div style={{
                  border: '2px solid var(--border-color)', borderRadius: '20px',
                  padding: '32px', marginBottom: '24px',
                  background: 'var(--bg-card)', transition: 'all 0.25s ease',
                  boxShadow: 'var(--glow)'
                }}>
                  {isProcessing ? (
                    <div style={{ textAlign: 'center', padding: '24px 0' }}>
                      <div style={{ width: '48px', height: '48px', border: '4px solid var(--border-color)', borderTopColor: 'var(--accent-primary)', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
                      <p style={{ color: 'var(--text-primary)', fontSize: '16px', fontWeight: '500' }}>{processingStage}</p>
                    </div>
                  ) : (
                    <>
                      <div style={{ fontSize: '40px', marginBottom: '16px', textAlign: 'center' }}>▶</div>
                      <input
                        type="text"
                        placeholder={t('upload.youtubeUrlPlaceholder')}
                        value={youtubeUrl}
                        onChange={e => setYoutubeUrl(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && canProcess && session) handleProcess(); }}
                        style={{
                          width: '100%', padding: '16px', borderRadius: '12px',
                          border: '2px solid var(--border-color)', background: 'var(--bg-secondary)',
                          color: 'var(--text-primary)', fontSize: '15px', outline: 'none',
                          fontFamily: 'inherit', boxSizing: 'border-box',
                          transition: 'border-color 0.2s'
                        }}
                        onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'}
                        onBlur={e => e.target.style.borderColor = 'var(--border-color)'}
                      />
                      <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '10px', textAlign: 'center' }}>
                        {t('upload.youtubeCaption')}
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* File Upload */}
              {inputMode === 'file' && (
              <div
                ref={dropZoneRef}
                style={{
                  border: '2px dashed var(--border-color)', borderRadius: '20px',
                  padding: '48px 32px', cursor: 'pointer', marginBottom: '24px',
                  minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'var(--bg-card)', transition: 'all 0.25s ease',
                  boxShadow: 'var(--glow)'
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => !isProcessing && fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*,audio/*,.mp3,.wav,.m4a"
                  onChange={(e) => handleFileSelect(e.target.files[0])}
                  style={{ display: 'none' }}
                />
                {isProcessing ? (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ width: '48px', height: '48px', border: '4px solid var(--border-color)', borderTopColor: 'var(--accent-primary)', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
                    <p style={{ color: 'var(--text-primary)', fontSize: '16px', fontWeight: '500' }}>{processingStage}</p>
                  </div>
                ) : file ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <span style={{ fontSize: '48px' }}>{fileType === 'video' ? '🎬' : '🎵'}</span>
                    <div style={{ textAlign: 'left' }}>
                      <span style={{ display: 'block', fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>{file.name}</span>
                      <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>{(file.size / (1024 * 1024)).toFixed(2)} MB</span>
                    </div>
                    <button
                      style={{ width: '36px', height: '36px', borderRadius: '50%', border: 'none', background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', fontSize: '18px', cursor: 'pointer' }}
                      onClick={(e) => { e.stopPropagation(); setFile(null); }}
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '56px', marginBottom: '16px' }}>📁</div>
                    <p style={{ fontSize: '18px', color: 'var(--text-primary)', fontWeight: '500', marginBottom: '8px' }}>{t('upload.dropFile')}</p>
                    <p style={{ fontSize: '14px', color: 'var(--text-muted)' }}>{t('upload.browseFiles')}</p>
                  </div>
                )}
              </div>
              )}

              {/* Output Type */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '24px' }}>
                <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>{t('upload.create')}</span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {[
                    { id: 'thread', label: '🧵 ' + t('upload.thread') },
                    { id: 'article', label: '📝 ' + t('upload.article') }
                  ].map(type => (
                    <button
                      key={type.id}
                      style={{
                        padding: '10px 20px', borderRadius: '10px',
                        border: `2px solid ${outputType === type.id ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                        background: outputType === type.id ? 'var(--accent-subtle-bg)' : 'transparent',
                        color: outputType === type.id ? 'var(--accent-primary)' : 'var(--text-secondary)',
                        fontSize: '14px', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit'
                      }}
                      onClick={() => setOutputType(type.id)}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {outputType === 'article' && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '20px' }}>
                  <label
                    style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '14px' }}
                    onClick={() => setIncludeTldr(!includeTldr)}
                  >
                    <div
                      style={{
                        width: '18px', height: '18px', borderRadius: '5px',
                        border: `2px solid ${includeTldr ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                        background: includeTldr ? 'var(--accent-primary)' : 'var(--bg-card)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.2s ease', flexShrink: 0, cursor: 'pointer'
                      }}
                    >
                      {includeTldr && <span style={{ color: 'white', fontSize: '12px', fontWeight: '700', lineHeight: 1 }}>✓</span>}
                    </div>
                    {t('upload.includeTldr')}
                  </label>
                </div>
              )}

              {/* Output Language Selector */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '24px' }}>
                <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>{t('upload.outputLanguage')}</span>
                <select
                  value={outputLanguage}
                  onChange={e => setOutputLanguage(e.target.value)}
                  style={{
                    padding: '10px 16px', borderRadius: '10px',
                    border: '2px solid var(--border-color)', background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)', fontSize: '14px', fontFamily: 'inherit',
                    cursor: 'pointer', outline: 'none', minWidth: '200px'
                  }}
                >
                  {Object.entries(t('outputLanguages', { returnObjects: true })).map(([code, label]) => (
                    <option key={code} value={code}>{label}</option>
                  ))}
                </select>
              </div>

              {/* Process Button */}
              <button
                style={{
                  width: '100%', padding: '18px', borderRadius: '14px', border: 'none',
                  background: 'var(--accent-gradient)', color: 'white',
                  fontSize: '17px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
                  opacity: !canProcess || isProcessing || !session ? 0.5 : 1,
                  transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)', transform: 'scale(1)',
                  boxShadow: !canProcess || isProcessing || !session ? 'none' : 'var(--shadow-btn)',
                  letterSpacing: '-0.01em'
                }}
                onClick={handleProcess}
                disabled={!canProcess || isProcessing || !session}
                onMouseEnter={e => { if (canProcess && !isProcessing && session) { e.target.style.transform = 'translateY(-1px) scale(1.01)'; e.target.style.boxShadow = 'var(--shadow-lg)'; } }}
                onMouseLeave={e => { e.target.style.transform = 'scale(1)'; e.target.style.boxShadow = canProcess && !isProcessing && session ? 'var(--shadow-btn)' : 'none'; }}
              >
                {!session ? t('auth.signInToTransform') : isProcessing ? t('upload.transforming') : t('upload.transform')}
              </button>

              <p style={{ marginTop: '20px', fontSize: '13px', color: 'var(--text-muted)', letterSpacing: '0.01em' }}>
                {t('app.footer')}
              </p>
            </div>
          </div>
        )}

        {/* Editor View */}
        {view === 'editor' && activeProject && (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1 }}>
            {/* Editor Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 24px', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-card)', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: '1 1 auto', minWidth: 0 }}>
                <input
                  type="text"
                  value={activeProject.name}
                  onChange={(e) => updateProjectName(e.target.value)}
                  style={{ background: 'transparent', border: '1.5px dashed var(--border-color)', borderRadius: '8px', padding: '4px 10px', color: 'var(--text-primary)', fontSize: '18px', fontWeight: '600', fontFamily: 'inherit', outline: 'none', minWidth: '120px', maxWidth: '400px', flex: '1 1 auto', transition: 'border-color 0.2s' }}
                  onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border-color)'}
                />
                {activeProject.type === 'thread' && (
                  <span style={{ padding: '6px 12px', borderRadius: '8px', background: 'var(--accent-subtle-bg)', color: 'var(--accent-primary)', fontSize: '13px', fontWeight: '500' }}>
                    🧵 {t('upload.thread')}
                  </span>
                )}
                {detectedLanguage && (
                  <span style={{ padding: '6px 12px', borderRadius: '8px', background: 'var(--accent-subtle-bg)', color: 'var(--success)', fontSize: '13px' }}>
                    🌍 {detectedLanguage.toUpperCase()}
                  </span>
                )}
                {contentNiche && (
                  <span style={{ padding: '6px 12px', borderRadius: '8px', background: 'var(--accent-subtle-bg)', color: 'var(--accent-secondary)', fontSize: '13px', fontWeight: '500' }}>
                    {contentNiche}
                  </span>
                )}
              </div>
              {activeProject.type === 'article' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 auto', justifyContent: 'center', paddingRight: '60px' }}>
                  {(contentVersions[activeProject.id] || []).length > 0 && (
                    <button
                      style={{ padding: '10px 18px', borderRadius: '10px', border: '2px solid var(--border-color)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '14px', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
                      onClick={handleUndoRefine}
                    >
                      ↩ Undo ({(contentVersions[activeProject.id] || []).length})
                    </button>
                  )}
                  <button
                    style={theme === 'dark'
                      ? { padding: '12px 24px', borderRadius: '12px', border: '2px solid var(--accent-primary)', background: 'var(--accent-subtle-bg)', color: 'var(--accent-primary)', fontSize: '15px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', transition: 'all 0.2s' }
                      : { padding: '12px 24px', borderRadius: '12px', border: 'none', background: 'var(--accent-gradient)', color: 'white', fontSize: '15px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', transition: 'all 0.2s' }
                    }
                    onClick={() => {
                      if (usage.tier !== 'pro' && refineCount >= 2) {
                        setShowPricingModal(true);
                        showToast('Free plan includes 2 AI refines. Upgrade to Pro for unlimited.', 'error');
                      } else {
                        setShowRegenerateModal(true);
                      }
                    }}
                    onMouseEnter={e => { if (theme === 'dark') { e.target.style.background = 'var(--accent-primary)'; e.target.style.color = 'white'; } }}
                    onMouseLeave={e => { if (theme === 'dark') { e.target.style.background = 'var(--accent-subtle-bg)'; e.target.style.color = 'var(--accent-primary)'; } }}
                  >
                    ✨ Refine with AI{usage.tier !== 'pro' && ` (${Math.max(0, 2 - refineCount)} left)`}
                  </button>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                {activeProject.type === 'thread' && (
                  <button
                    style={{ padding: '10px 16px', borderRadius: '10px', border: 'none', background: 'var(--accent-gradient)', color: 'white', fontSize: '14px', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
                    onClick={() => setShowHookGenerator(true)}
                  >
                    🎯 Hooks
                  </button>
                )}
                {activeProject.type === 'thread' && (
                  <button
                    style={{ padding: '10px 16px', borderRadius: '10px', border: 'none', background: 'var(--accent-gradient)', color: 'white', fontSize: '14px', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
                    onClick={() => setShowSchedulingModal(true)}
                  >
                    📅 Schedule
                  </button>
                )}
                <button
                  style={{ padding: '10px 20px', borderRadius: '10px', border: '2px solid var(--border-color)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '14px', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
                  onClick={() => setShowExportModal(true)}
                >
                  {t('editor.export')}
                </button>
                <LanguageSelector />
                <ThemeToggle theme={theme} onToggle={toggleTheme} />
              </div>
            </div>

            {/* Editor Content */}
            <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
              {/* Preview Panel */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--border-color)', minHeight: 0 }}>
                <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border-light)', fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {t('editor.preview')}
                </div>
                <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
                  {activeProject.type === 'thread' ? (
                    extractTweetsFromContent(activeProject.content).map((tweet, i) => (
                      <div key={i} style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '16px', padding: '20px', marginBottom: '12px' }}>
                        <p style={{ color: 'var(--text-primary)', lineHeight: '1.6', margin: 0 }}>{tweet}</p>
                        <span style={{ display: 'block', marginTop: '12px', fontSize: '12px', color: tweet.length > 280 ? 'var(--error)' : 'var(--text-muted)' }}>
                          {tweet.length}/280 {tweet.length > 280 && '⚠️ ' + t('editor.tooLong')}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div style={{ maxWidth: '700px' }}>
                      {activeProject.banner_url && (
                        <img src={activeProject.banner_url} alt="Banner" style={{ width: '100%', borderRadius: '12px', marginBottom: '24px', objectFit: 'cover', maxHeight: '300px' }} />
                      )}
                      <div className="article-preview" dangerouslySetInnerHTML={{ __html: markdownToHtml(activeProject.content) }} />
                    </div>
                  )}
                </div>
              </div>

              {/* Edit Panel */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border-light)', fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {t('editor.edit')}
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
                    {/* Virality Score — hidden until scoring is backed by real engagement data */}
                    {/* <ViralityScoreDisplay score={viralityScore} onRescore={rescoreVirality} isRescoring={isRescoringVirality} /> */}
                    {/* Banner Upload Zone (articles only) */}
                    {activeProject.type !== 'thread' && (
                      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-light)' }}>
                        <input ref={bannerInputRef} type="file" accept="image/*" onChange={handleBannerUpload} style={{ display: 'none' }} />
                        {activeProject.banner_url ? (
                          <div style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden' }}>
                            <img src={activeProject.banner_url} alt="Banner" style={{ width: '100%', maxHeight: '120px', objectFit: 'cover', borderRadius: '8px' }} />
                            <button
                              onClick={removeBanner}
                              style={{
                                position: 'absolute', top: '8px', right: '8px',
                                padding: '4px 10px', borderRadius: '6px',
                                background: 'rgba(0,0,0,0.6)', border: 'none',
                                color: 'white', fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit'
                              }}
                            >
                              {t('editor.removeBanner')}
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => bannerInputRef.current?.click()}
                            style={{
                              width: '100%', padding: '16px', borderRadius: '8px',
                              border: '2px dashed var(--border-color)', background: 'transparent',
                              color: 'var(--text-muted)', fontSize: '13px', cursor: 'pointer',
                              fontFamily: 'inherit', transition: 'all 0.2s'
                            }}
                          >
                            {t('editor.addBanner')}
                          </button>
                        )}
                      </div>
                    )}
                    <EditorToolbar editor={editor} />
                    <div style={{ flex: 1, overflow: 'auto', background: 'var(--bg-primary)' }}>
                      <EditorContent editor={editor} style={{ height: '100%' }} />
                    </div>
                  </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Modals */}
      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} onAuth={(session) => { setSession(session); setShowAuthModal(false); showToast(t('auth.welcomeBack'), 'success'); }} />}

      {showExportModal && (
        <ExportModal
          activeProject={activeProject}
          onClose={() => setShowExportModal(false)}
          exportAsText={exportAsText}
          openXComposer={openXComposer}
          openThreadsComposer={openThreadsComposer}
        />
      )}

      {/* Performance Report Modal */}
      {/* Revenue Multiplier Modals */}
      {showBatchProcessor && (
        <BatchProcessor
          session={session}
          outputType={outputType}
          selectedTemplate={'file-matched'}
          onBatchComplete={(newProjects) => {
            loadProjects();
            showToast(`Batch complete! ${newProjects.length} projects created.`, 'success');
          }}
          onClose={() => setShowBatchProcessor(false)}
        />
      )}

      {showHookGenerator && activeProject && (
        <HookGenerator
          session={session}
          projectId={activeProject.id}
          content={activeProject.content}
          onHookSelected={(hook) => {
            // Replace first tweet with selected hook
            const tweets = extractTweetsFromContent(activeProject.content);
            if (tweets.length > 0) {
              tweets[0] = hook.text;
              updateProjectContent(tweets.join('\n\n---\n\n'));
              showToast('Hook applied to first tweet!', 'success');
            }
          }}
          onClose={() => setShowHookGenerator(false)}
        />
      )}

      {showSchedulingModal && activeProject && (
        <SchedulingModal
          session={session}
          projectId={activeProject.id}
          content={activeProject.content}
          contentType={activeProject.type}
          showToast={showToast}
          onScheduled={(data) => {
            showToast(data.message || 'Post scheduled successfully!', 'success');
            setShowSchedulingModal(false);
          }}
          onClose={() => setShowSchedulingModal(false)}
        />
      )}

      {/* Pricing Modal */}
      {showPricingModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(8px)' }} onClick={() => setShowPricingModal(false)}>
          <div style={{ background: 'var(--bg-card)', borderRadius: '20px', padding: '40px', width: '100%', maxWidth: '480px', position: 'relative', boxShadow: 'var(--shadow-lg)', border: '1px solid var(--border-color)' }} onClick={e => e.stopPropagation()}>
            <button style={{ position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '24px', cursor: 'pointer' }} onClick={() => setShowPricingModal(false)}>×</button>
            <div style={{ textAlign: 'center', marginBottom: '28px' }}>
              <div style={{ fontSize: '40px', marginBottom: '8px' }}>⭐</div>
              <h2 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '8px' }}>Upgrade to Pro</h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Unlimited transforms and AI refines</p>
            </div>

            {/* Billing toggle */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '24px' }}>
              {[
                { id: 'monthly', label: '$12/mo' },
                { id: 'semiannual', label: '$54/6mo', badge: 'Save 25%' },
                { id: 'yearly', label: '$99/yr', badge: 'Save 31%' }
              ].map(cycle => (
                <button
                  key={cycle.id}
                  onClick={() => setPricingBillingCycle(cycle.id)}
                  style={{
                    padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '600',
                    border: pricingBillingCycle === cycle.id ? 'none' : '1px solid var(--border-color)',
                    background: pricingBillingCycle === cycle.id ? 'var(--accent-gradient)' : 'transparent',
                    color: pricingBillingCycle === cycle.id ? 'white' : 'var(--text-secondary)',
                    cursor: 'pointer', fontFamily: 'inherit'
                  }}
                >
                  {cycle.label}
                  {cycle.badge && <span style={{ marginLeft: '6px', fontSize: '11px', opacity: 0.9 }}>{cycle.badge}</span>}
                </button>
              ))}
            </div>

            {/* Feature list */}
            <div style={{ marginBottom: '24px', padding: '0 8px' }}>
              {[
                { free: '3 transforms/week', pro: 'Unlimited transforms' },
                { free: '2 AI refines (lifetime)', pro: 'Unlimited AI refines' },
                { free: 'Voice DNA learning', pro: 'Voice DNA learning' },
                { free: 'Basic export', pro: 'All export formats' },
                { free: '', pro: 'Priority support' }
              ].map((row, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-light)', fontSize: '13px' }}>
                  <span style={{ color: 'var(--text-muted)' }}>{row.free}</span>
                  <span style={{ color: 'var(--text-primary)', fontWeight: '600' }}>{row.pro}</span>
                </div>
              ))}
            </div>

            <button
              onClick={async () => {
                try {
                  const res = await fetch(`${API_URL}/api/create-checkout-session`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ plan: pricingBillingCycle })
                  });
                  const data = await res.json();
                  if (data.url) window.location.href = data.url;
                  else showToast(data.error || 'Could not start checkout', 'error');
                } catch {
                  showToast('Could not start checkout', 'error');
                }
              }}
              style={{
                width: '100%', padding: '16px', borderRadius: '12px', border: 'none',
                background: 'var(--accent-gradient)', color: 'white',
                fontSize: '16px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit',
                transition: 'transform 0.2s'
              }}
            >
              Subscribe — {{ monthly: '$12/month', semiannual: '$54/6 months', yearly: '$99/year' }[pricingBillingCycle]}
            </button>
            <p style={{ textAlign: 'center', marginTop: '12px', fontSize: '12px', color: 'var(--text-muted)' }}>
              Cancel anytime from your profile. Secure payments by LemonSqueezy.
            </p>
          </div>
        </div>
      )}

      {/* Regenerate Feedback Modal */}
      {showRegenerateModal && activeProject && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => !isRegenerating && setShowRegenerateModal(false)}>
          <div style={{ background: 'var(--bg-card)', borderRadius: '16px', padding: '28px', width: '480px', maxWidth: '90vw', boxShadow: 'var(--shadow-lg)' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 8px 0', color: 'var(--text-primary)', fontSize: '18px' }}>✨ Refine with AI</h3>
            <p style={{ margin: '0 0 16px 0', color: 'var(--text-secondary)', fontSize: '14px' }}>Tell the AI how to adjust your article — tone, structure, length, style, or anything else. Your preferences are remembered for future articles.</p>
            <textarea
              value={regenerateFeedback}
              onChange={e => setRegenerateFeedback(e.target.value)}
              placeholder='e.g. "Make it more casual", "Shorter paragraphs", "Add more examples", "Less formal tone"'
              style={{ width: '100%', minHeight: '100px', padding: '12px', borderRadius: '10px', border: '2px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical', outline: 'none', boxSizing: 'border-box' }}
              disabled={isRegenerating}
              autoFocus
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
              <button
                style={{ padding: '10px 20px', borderRadius: '10px', border: '2px solid var(--border-color)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '14px', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit' }}
                onClick={() => { setShowRegenerateModal(false); setRegenerateFeedback(''); }}
                disabled={isRegenerating}
              >
                Cancel
              </button>
              <button
                style={{ padding: '10px 20px', borderRadius: '10px', border: 'none', background: 'var(--accent-gradient)', color: 'white', fontSize: '14px', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit', opacity: !regenerateFeedback.trim() || isRegenerating ? 0.5 : 1 }}
                onClick={handleRegenerate}
                disabled={!regenerateFeedback.trim() || isRegenerating}
              >
                {isRegenerating ? 'Refining...' : 'Refine'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAnalyticsDashboard && (
        <AnalyticsDashboard
          session={session}
          projects={projects}
          onClose={() => setShowAnalyticsDashboard(false)}
        />
      )}

      {showRevenueTracker && (
        <RevenueTracker
          session={session}
          showToast={showToast}
          onClose={() => setShowRevenueTracker(false)}
        />
      )}

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Global Styles */}
      <style>{globalStyles}</style>
    </div>
  );
};

export default App;
